// frontend-mvp/src/utils/listingUpgradePayload.js

const safeArray = (arr) => (Array.isArray(arr) ? arr : []);

const htmlToPlainText = (html) => {
  const raw = (html ?? '').toString();
  if (!raw.trim()) return '';

  // Browser-safe parsing (preferred)
  if (typeof DOMParser !== 'undefined') {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(raw, 'text/html');
      // Remove scripts/styles/noscript
      doc.querySelectorAll('script,style,noscript').forEach((n) => n.remove());
      const text = (doc.body?.textContent || '').replace(/\s+\n/g, '\n').replace(/\n\s+/g, '\n');
      return text.replace(/[ \t]{2,}/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
    } catch (e) {
      // fall through to regex fallback
    }
  }

  // Fallback: strip scripts/styles + tags
  return raw
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<noscript[\s\S]*?<\/noscript>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h1|h2|h3|h4|h5|h6|li)>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const normalizeComponentKey = (component) => {
  const raw = (component || '').toString().toLowerCase().trim();
  const normalized = raw.replace(/\s+/g, '_');

  if (normalized.includes('title')) return 'title';
  if (normalized.includes('bullet')) return 'bullet_points';
  if (normalized.includes('a_plus') || normalized.includes('a+')) return 'a_plus_content';
  if (normalized.includes('description')) return 'description';
  if (normalized.includes('image')) return 'images';
  if (normalized.includes('additional')) return 'additional_content';
  return normalized || 'additional_content';
};

const toActionPointStrings = (points) =>
  safeArray(points)
    .map((p) => {
      if (typeof p === 'string') return p.trim();
      if (p && typeof p === 'object') return (p.implementation || p.action || '').toString().trim();
      return '';
    })
    .filter(Boolean);

/**
 * Builds a JSON payload to transfer listing + analysis into the single-listing upgrade flow.
 * Each "item" includes prompt, action_points, and implementation_guide.
 */
export const buildListingUpgradePayload = (productData) => {
  const analysis = productData?.analysis || {};
  const componentScores = analysis?.component_scores || {};
  const recommendations = safeArray(analysis?.recommendations);

  const hasAPlus = (componentScores?.a_plus_content?.score || 0) > 0;
  const imagesComponentKey = componentScores?.b_plus_images ? 'b_plus_images' : 'images';
  const contentComponentKey = hasAPlus ? 'a_plus_content' : 'description';

  const getTopRecommendation = (componentKey) => {
    const normalizedTarget = normalizeComponentKey(componentKey);
    const matched = recommendations
      .map((r) => ({ ...r, _normalizedComponent: normalizeComponentKey(r?.component) }))
      .filter((r) => {
        const c = r._normalizedComponent;
        if (normalizedTarget === 'images') return c === 'images' || c === 'b_plus_images';
        if (normalizedTarget === 'description') return c === 'description' || c === 'a_plus_content';
        return c === normalizedTarget;
      })
      .sort((a, b) => (a?.priority || 999) - (b?.priority || 999));

    return matched[0] || null;
  };

  const getComponentItem = ({ key, label, current_value, componentKeyForScores }) => {
    const scoreKey = componentKeyForScores || key;
    const scoreData = componentScores?.[scoreKey] || {};
    const rec = getTopRecommendation(scoreKey);

    const rawActionPoints = safeArray(scoreData?.detailed_action_points || scoreData?.action_points);

    return {
      key,
      label,
      current_value,
      prompt: (rec?.recommendation || rec?.issue || '').toString(),
      action_points: toActionPointStrings(rawActionPoints),
      implementation_guide: (rec?.implementation_steps || '').toString(),
      // Keep extra context for downstream rendering if needed
      expected_impact: (rec?.expected_impact || '').toString(),
      action_points_raw: rawActionPoints,
      recommendation_raw: rec,
      component_score: scoreData?.score ?? null,
      component_max_score: scoreData?.max_score ?? null
    };
  };

  const images = safeArray(productData?.all_images || productData?.b_plus_images);
  const aPlusBody =
    typeof productData?.a_plus_content?.body === 'string' ? productData.a_plus_content.body : '';
  const aPlusBodyText = htmlToPlainText(aPlusBody);
  const descriptionText = htmlToPlainText(productData?.description || '');
  const descriptionFallback =
    descriptionText || aPlusBodyText || '';
  const payload = {
    version: 'listing-upgrade-payload/v1',
    created_at: new Date().toISOString(),
    asin: productData?.asin || '',
    listing: {
      asin: productData?.asin || '',
      title: productData?.title || '',
      bullet_points: safeArray(productData?.bullet_points),
      description: descriptionFallback,
      main_image: productData?.main_image || '',
      all_images: images,
      image_details: safeArray(productData?.image_details),
      a_plus_content: productData?.a_plus_content ?? null,
      specifications: safeArray(productData?.specifications)
    },
    analysis: analysis,
    items: [
      getComponentItem({
        key: 'title',
        label: 'Title',
        current_value: productData?.title || '',
        componentKeyForScores: 'title'
      }),
      getComponentItem({
        key: 'bullet_points',
        label: 'Bullet Points',
        current_value: safeArray(productData?.bullet_points),
        componentKeyForScores: 'bullet_points'
      }),
      getComponentItem({
        key: 'description',
        label: hasAPlus ? 'A+ Content' : 'Description',
        current_value: hasAPlus ? (productData?.a_plus_content ?? null) : (productData?.description || ''),
        componentKeyForScores: contentComponentKey
      }),
      getComponentItem({
        key: 'images',
        label: 'Images',
        current_value: {
          main_image: productData?.main_image || '',
          all_images: images
        },
        componentKeyForScores: imagesComponentKey
      })
    ]
  };

  return payload;
};


